import React, { useMemo } from 'react';
import {
  TokenCardProvider,
  type TokenCardDependencies,
  type TokenCardToastFn,
} from './TokenCardContext';

/**
 * Props for wiring the cash token card into a Vaiu app.
 * Pass your app’s hooks and payment subcomponents — the package cannot import `@/` paths.
 */
export type CashTokenCardProviderProps = {
  children: React.ReactNode;
  /** App hook, e.g. `useToast` from `@/hooks/use-toast` */
  useToast: () => { toast: TokenCardToastFn };
  useAuth: () => { user: TokenCardDependencies['user'] };
  useConfig: () => { config: TokenCardDependencies['config'] };
  boPaymentsApi: TokenCardDependencies['boPaymentsApi'];
  updateTokenConditions: TokenCardDependencies['updateTokenConditions'];
  components: Pick<
    TokenCardDependencies,
    'PrepaidCard' | 'NFCPayment' | 'InvoicePaymentDecision'
  >;
};

/**
 * Single shared provider for consumer, merchant, and developer apps.
 * Each app passes its context hooks and local payment components.
 */
export function CashTokenCardProvider({
  children,
  useToast: useAppToast,
  useAuth: useAppAuth,
  useConfig: useAppConfig,
  boPaymentsApi,
  updateTokenConditions,
  components,
}: CashTokenCardProviderProps) {
  const { toast } = useAppToast();
  const { user } = useAppAuth();
  const { config } = useAppConfig();

  const value: TokenCardDependencies = useMemo(
    () => ({
      toast,
      user,
      config,
      boPaymentsApi,
      updateTokenConditions,
      PrepaidCard: components.PrepaidCard,
      NFCPayment: components.NFCPayment,
      InvoicePaymentDecision: components.InvoicePaymentDecision,
    }),
    [toast, user, config, boPaymentsApi, updateTokenConditions, components]
  );

  return <TokenCardProvider value={value}>{children}</TokenCardProvider>;
}
